import { useEffect, useRef } from "react";
import { useParams } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { setCurrentStore } from "@/store/slices/auth.slice";
import { setStoreContext } from "@/store/slices/tenant.slice";
import { resetStoreSpecificState } from "@/store/actions/storeActions";
import type { RootState } from "@/store/store";

/**
 * Component that synchronizes the current store ID from URL parameters
 * with the Redux state for API client URL replacement.
 * Also resets store-specific state when switching between stores.
 */
export default function RouteStoreSync() {
  const dispatch = useDispatch();
  const params = useParams();
  const { currentStoreId, verifiedStores } = useSelector(
    (state: RootState) => state.auth,
  );

  // Track the previous store ID to detect store changes
  const previousStoreIdRef = useRef<string | null>(null);

  useEffect(() => {
    // Extract storeId and storeType from URL parameters
    const urlStoreId = params.storeId as string | undefined;
    const urlStoreType = params.storeType as string | undefined;

    console.log(`[RouteStoreSync] useEffect triggered:`, {
      urlStoreId,
      urlStoreType,
      currentStoreId,
      previousStoreId: previousStoreIdRef.current,
    });

    // Only proceed if we have URL parameters for a seller store.
    if (!urlStoreId || urlStoreType !== "seller") {
      console.log(`[RouteStoreSync] Skipping - invalid params:`, { urlStoreId, urlStoreType });
      return;
    }

    const previousStoreId = previousStoreIdRef.current;

    // Check if store has changed - if so, reset all store-specific state
    // This ensures data from the previous store doesn't persist
    if (previousStoreId && previousStoreId !== urlStoreId) {
      console.log(
        `[RouteStoreSync] 🔄 STORE CHANGED! Resetting store-specific state`,
      );
      console.log(`[RouteStoreSync] Previous: ${previousStoreId}`);
      console.log(`[RouteStoreSync] New: ${urlStoreId}`);
      dispatch(resetStoreSpecificState());
      console.log(`[RouteStoreSync] ✅ resetStoreSpecificState dispatched`);
    } else {
      console.log(`[RouteStoreSync] No store change detected:`, {
        previousStoreId,
        urlStoreId,
        isFirstLoad: !previousStoreId,
        isSameStore: previousStoreId === urlStoreId,
      });
    }

    // Update the ref to track the current URL store ID
    previousStoreIdRef.current = urlStoreId;
    console.log(`[RouteStoreSync] Updated previousStoreIdRef to: ${urlStoreId}`);

    // Check if the current store ID in Redux matches the URL
    if (currentStoreId === urlStoreId) {
      return; // Already synchronized
    }

    // Find the store in verified stores list
    const matchingStore = verifiedStores.find(
      (store) =>
        store.storeId === urlStoreId && store.storeType === urlStoreType,
    );

    if (!matchingStore) {
      console.log(
        `[RouteStoreSync] Skipping store sync until verified store is available: ${urlStoreType}/${urlStoreId}`,
      );
      return;
    }

    // Update Redux state with the store from URL
    // Dispatch to both auth slice and tenant slice to keep them in sync
    dispatch(setCurrentStore(matchingStore));
    dispatch(setStoreContext({ storeId: urlStoreId, storeType: urlStoreType }));
    console.log(
      `[RouteStoreSync] Set current store to: ${urlStoreType}/${urlStoreId}`,
    );
  }, [
    params.storeId as string | undefined,
    params.storeType as string | undefined,
    currentStoreId,
    verifiedStores,
    dispatch,
  ]);

  // This component doesn't render anything
  return null;
}
